import { memo, type ReactElement, useMemo, useCallback } from "react";
import type { FiatPrice } from "@b2bportal/purchase-api";
import { CyCheckoutReviewSelectors } from "@hopper-b2b/cypress";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  type FarePricing,
  type ISummaryLineItem,
  SummaryLineItemBaseEnum,
  TripCategory,
} from "@hopper-b2b/types";
import { Box, Typography } from "@material-ui/core";
import type { Variant } from "@material-ui/core/styles/createTypography";
import clsx from "clsx";
import { Icon, IconName } from "../Icon";
import { SecurePaymentsBanner } from "../SecurePaymentsBanner";
import "./styles.scss";
import { Slot } from "../Slots";

interface PricingBreakdownTitles {
  baseFareText: string;
  frozenBaseFareText?: string;
  taxesAndFeesText: string | ReactElement;
  totalText: string;
  seatsText?: string;
  rewardsAppliedText: (accountName: string) => string;
  rewardsAccountText: (lastFour?: string) => string;
}

export interface PricingBreakdownProps {
  className?: string;
  pricingItems?: IPricingLineItem[];
  summaryItems: ISummaryLineItem[];
  showSecurePaymentsBanner?: boolean;
  titles?: Partial<PricingBreakdownTitles>;
  initialPassengerPricing?: FarePricing;
  tripCategory?: TripCategory;
  currencyString?: string;
  modalTitle?: string;
  priceItemIcon?: ReactElement;
  perTraveler?: boolean;
  useHeader?: boolean;
  displayProductsSeparately?: boolean;
  roundingMode?: "ceil" | "halfCeil";
}

export interface IPricingLineItem {
  lineTitle?: string;
  customLineItemText?: string;
  customLineItem?: ReactElement;
  customItemDescriptionText?: string;
  baseAmount: FiatPrice;
  changeFee?: FiatPrice;
  taxesAndFees?: FiatPrice;
  seats?: FiatPrice;
  icon?: ReactElement;
  frozenPrice?: boolean;
  products?: Record<string, FiatPrice>;
  useHtml?: boolean;
  className?: string;
  // Test data identifier for easier testing, added unto the corresponding element
  dataTestId?: string;
}

interface PriceItemProps {
  baseAmount: FiatPrice;
  taxesAndFees?: FiatPrice;
  titles: {
    itemText: string | ReactElement;
    description?: string;
    taxesAndFeesText: string | ReactElement;
    changeFeeText?: string;
    seatsText?: string;
  };
  changeFee?: FiatPrice;
  lineTitle?: string;
  seats?: FiatPrice;
  icon?: ReactElement;
  priceItemIcon?: ReactElement;
  products?: Record<string, FiatPrice>;
  useHtml?: boolean;
  className?: string;
  // Test data identifier for easier testing, added unto the corresponding element
  dataTestId?: string;
}

const Products = ({ products }: { products: Record<string, FiatPrice> }) => {
  const { t, formatFiatCurrency } = useI18nContext();

  return (
    <>
      {Object.keys(products).map((product) => (
        <Box
          className={clsx("price-line", "fees", product)}
          key={`price-line-${product}`}
        >
          <Typography className="label" variant="subtitle2">
            {t(product)}
          </Typography>
          <Typography className="value" variant="subtitle2">
            {formatFiatCurrency(products[product])}
          </Typography>
        </Box>
      ))}
    </>
  );
};

const PriceItem = ({
  baseAmount,
  taxesAndFees,
  titles: { itemText, description, taxesAndFeesText, changeFeeText, seatsText },
  changeFee,
  lineTitle,
  seats,
  icon,
  priceItemIcon,
  products,
  useHtml,
  className,
  dataTestId,
}: PriceItemProps) => {
  const { formatFiatCurrency } = useI18nContext();

  return (
    <div className="pricing-item-container">
      {priceItemIcon ? priceItemIcon : null}
      <Box className={clsx("pricing-item-wrapper", className)}>
        {lineTitle && (
          <Box className={clsx("price-line", "title")}>
            <Typography noWrap variant="subtitle1" data-dd-privacy="mask">
              {lineTitle}
            </Typography>
          </Box>
        )}
        <Box className={clsx("pricing-item-body", { "with-icon": !!icon })}>
          {icon && <Box className="icon-section">{icon}</Box>}
          <Box className="content-section">
            <Box className={clsx("price-line", "base")}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
              >
                {useHtml && typeof itemText === "string" ? (
                  <Typography
                    noWrap
                    className="label"
                    variant="subtitle2"
                    dangerouslySetInnerHTML={{ __html: itemText }}
                  />
                ) : (
                  <Typography noWrap className="label" variant="subtitle2">
                    {itemText}
                  </Typography>
                )}
                <Typography noWrap className="description" variant="caption">
                  {description}
                </Typography>
              </Box>
              <Typography
                noWrap
                className="value"
                variant="subtitle2"
                data-test={dataTestId}
              >
                {formatFiatCurrency(baseAmount)}
              </Typography>
            </Box>
            {changeFee != null && (
              <Box className="price-line fees">
                <Typography className="label" variant="subtitle2">
                  {changeFeeText || "Change Fee"}
                </Typography>
                <Typography className="value" variant="subtitle2">
                  {formatFiatCurrency(changeFee)}
                </Typography>
              </Box>
            )}
            {seats != null && (
              <Box className={clsx("price-line", "fees")}>
                <Typography className="label" variant="subtitle2">
                  {seatsText || "Seat Selection"}
                </Typography>
                <Typography className="value" variant="subtitle2">
                  {formatFiatCurrency(seats)}
                </Typography>
              </Box>
            )}
            {products ? <Products products={products} /> : null}
            {taxesAndFees !== undefined ? (
              <Box className={clsx("price-line", "fees")}>
                <Typography className="label" variant="subtitle2">
                  {taxesAndFeesText}
                </Typography>
                <Typography
                  className="value"
                  variant="subtitle2"
                  data-test={
                    CyCheckoutReviewSelectors.PRICE_BREAKDOWN_TAXES_AND_FEES_AMOUNT
                  }
                >
                  {formatFiatCurrency(taxesAndFees)}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </div>
  );
};

interface SummaryItemProps {
  summaryLineItem: ISummaryLineItem;
  titles: {
    totalText: string;
    rewardsAppliedText?: (accountName: string) => string;
    rewardsAccountText?: (lastFour?: string) => string;
  };
  roundingMode?: "ceil" | "halfCeil";
}

type SummaryItemContent = {
  icon?: ReactElement;
  titleVariant?: Variant;
  title: string;
  details?: string;
  priceVariant?: Variant;
  price: string;
  priceSubstring?: string;
  dataTest?: string;
};

const SummaryItem = memo(
  ({
    summaryLineItem,
    titles: { totalText },
    roundingMode,
  }: SummaryItemProps) => {
    const { t, formatFiatCurrency: formatFiatFunctionInternal } =
      useI18nContext();
    const formatFiatCurrency = useCallback(
      (value: FiatPrice) =>
        // @ts-expect-error this function is not correctly typed from the lib but it does take this property
        formatFiatFunctionInternal(value, { roundingMode }),
      [formatFiatFunctionInternal, roundingMode]
    );

    const summaryItemContent: SummaryItemContent = useMemo(() => {
      switch (summaryLineItem.type) {
        case SummaryLineItemBaseEnum.TOTAL:
          return {
            titleVariant: "subtitle1",
            title: summaryLineItem.label ?? totalText,
            details: summaryLineItem.details,
            price: formatFiatCurrency(summaryLineItem.fiatPrice),
            dataTest: CyCheckoutReviewSelectors.PRICE_BREAKDOWN_TOTAL_AMOUNT,
          };
        case SummaryLineItemBaseEnum.PAYMENT:
          return {
            icon: (
              <Icon name={IconName.Payment} className="pricing-item-icon" />
            ),
            title: "",
            price: formatFiatCurrency(summaryLineItem.fiatPrice),
          };
        case SummaryLineItemBaseEnum.CUSTOM:
          return {
            titleVariant: "subtitle1",
            title: summaryLineItem.label ?? "",
            priceVariant: "subtitle2",
            price: formatFiatCurrency(summaryLineItem.fiatPrice),
            icon: summaryLineItem.icon,
          };
        case SummaryLineItemBaseEnum.PRODUCT:
          return {
            titleVariant: "subtitle1",
            title: t(summaryLineItem.product),
            details: summaryLineItem.perPaxPrice
              ? `${formatFiatCurrency(
                  summaryLineItem.perPaxPrice
                )} per traveler`
              : undefined,
            price: formatFiatCurrency(summaryLineItem.fiatPrice),
          };
        case SummaryLineItemBaseEnum.PRICE_FREEZE_DISCOUNT:
          return {
            title: t("priceFreezeDiscountText") as string,
            price: `-${formatFiatCurrency(summaryLineItem.fiatPrice)}`, //Negative value to render as a discount
          };
        case SummaryLineItemBaseEnum.CHFAR_DISCOUNT:
          return {
            title: t("chfarDiscountText") as string,
            price: `-${formatFiatCurrency(summaryLineItem.fiatPrice)}`, //Negative value to render as a discount
          };
        case SummaryLineItemBaseEnum.VOUCHER_DISCOUNT:
          return {
            title: t("voucherDiscountText") as string,
            price: `-${formatFiatCurrency(summaryLineItem.fiatPrice)}`, //Negative value to render as a discount
          };
        case SummaryLineItemBaseEnum.CREDIT_PAYMENT:
          return {
            title: t("creditPaymentText") as string,
            price: `-${formatFiatCurrency(summaryLineItem.fiatPrice)}`, //Negative value to render as a discount
          };
        case SummaryLineItemBaseEnum.PROMOTION_DISCOUNT:
          return {
            title: t("promotionDiscountText") as string,
            price: `-${formatFiatCurrency(summaryLineItem.fiatPrice)}`, //Negative value to render as a discount
          };
        case SummaryLineItemBaseEnum.REWARDS:
          return {
            titleVariant: "subtitle1",
            title: t("pointsApplied"),
            price: `-${summaryLineItem.rewardsPrice.value} ${summaryLineItem.rewardsPrice.currency}`,
          };
        case SummaryLineItemBaseEnum.CARD_PAYMENT:
          return {
            icon: (
              <Icon name={IconName.Payment} className="pricing-item-icon" />
            ),
            title: `${summaryLineItem.label} ${summaryLineItem.lastFour}`,
            price: `-${formatFiatCurrency(summaryLineItem.fiatPrice)}`,
          };
        case SummaryLineItemBaseEnum.AMOUNT_DUE:
          return {
            title: t("amountDue"),
            price: `${formatFiatCurrency(summaryLineItem.fiatPrice)}`,
            dataTest: CyCheckoutReviewSelectors.PRICE_BREAKDOWN_DUE_TODAY,
          };
        default:
          return {
            title: "",
            price: "",
          };
      }
    }, [summaryLineItem, totalText, t, formatFiatCurrency]);

    return (
      <Box className={clsx("summary-item-wrapper", summaryLineItem.type)}>
        <Box className={clsx("summary-line-section", "left")}>
          {summaryItemContent.icon}
          <Box className="label">
            <Typography
              variant={summaryItemContent.titleVariant ?? "subtitle2"}
              component="p"
            >
              {summaryItemContent.title}
            </Typography>
            {summaryItemContent.details && (
              <Typography className="label-details" variant="subtitle2">
                {summaryItemContent.details}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className={clsx("summary-line-section", "right")}>
          <Typography
            className={clsx("value", "fiat")}
            variant={summaryItemContent.priceVariant ?? "subtitle1"}
            component="p"
            data-test={summaryItemContent.dataTest}
          >
            {summaryItemContent.price}
          </Typography>
          {summaryItemContent.priceSubstring ? (
            <Typography
              className={clsx("value", "rewards")}
              variant={summaryItemContent.priceVariant ?? "subtitle1"}
              component="p"
            >
              {summaryItemContent.priceSubstring}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  }
);

export const PricingBreakdown = ({
  className,
  pricingItems,
  summaryItems,
  showSecurePaymentsBanner = false,
  titles: lineTitles,
  initialPassengerPricing,
  tripCategory,
  currencyString,
  modalTitle,
  priceItemIcon,
  useHeader = false,
  perTraveler = false,
  displayProductsSeparately,
  roundingMode = "ceil",
}: PricingBreakdownProps): JSX.Element => {
  const { t } = useI18nContext();
  const titles = {
    baseFareText: t("baseFare"),
    changeFeeText: t("changeFee"),
    frozenBaseFareText: t("frozenBaseFare"),
    rewardsAccountText: (lastFour?: string) =>
      lastFour
        ? `${t("endingInWithLastFour", { lastFour })}: `
        : `${t("paymentMethodSubtitleAmount")}: `,
    rewardsAppliedText: (accountName: string) =>
      `${t("appliedWithAccountName", { accountName })}:`,
    seatsText: t("seatSelection"),
    taxesAndFeesText: t("taxesAndFees"),
    totalText: perTraveler ? t("totalPerTraveler") : t("total"),
    ...lineTitles,
  };

  const content = {
    oneWayPerTraveler: t("oneWayPerTraveler"),
    roundTripPerTraveler: t("roundTripPerTraveler"),
    emptyLineItems: t("emptyLineItems"),
  };
  const LineItems = pricingItems?.length ? (
    pricingItems.map((item, index) => (
      <PriceItem
        key={`price-item-${index}`}
        baseAmount={item.baseAmount}
        taxesAndFees={item.taxesAndFees}
        titles={{
          itemText:
            item.customLineItem ??
            item.customLineItemText ??
            (item.frozenPrice
              ? (titles.frozenBaseFareText ?? titles.baseFareText)
              : titles.baseFareText),
          description: item.customItemDescriptionText,
          taxesAndFeesText: titles.taxesAndFeesText,
          seatsText: titles.seatsText,
        }}
        changeFee={item.changeFee}
        lineTitle={item.lineTitle}
        seats={item.seats}
        icon={item.icon}
        priceItemIcon={priceItemIcon}
        products={!displayProductsSeparately ? item.products : undefined}
        useHtml={item.useHtml}
        className={item.className}
        dataTestId={item.dataTestId}
      />
    ))
  ) : initialPassengerPricing?.baseAmount ? (
    <>
      <Typography
        className="initial-passenger-pricing-title"
        variant="subtitle1"
      >
        {tripCategory === TripCategory.ONE_WAY
          ? content.oneWayPerTraveler
          : content.roundTripPerTraveler}
      </Typography>
      <PriceItem
        key={"price-item-initial"}
        baseAmount={initialPassengerPricing.baseAmount.fiat}
        taxesAndFees={initialPassengerPricing.taxAmount.fiat}
        titles={{
          itemText: titles.baseFareText,
          taxesAndFeesText: titles.taxesAndFeesText,
          seatsText: titles.seatsText,
        }}
      />
    </>
  ) : (
    <Typography className="empty-line-items">
      {content.emptyLineItems}
    </Typography>
  );
  const products =
    Object.keys(pricingItems?.[0]?.products ?? []).length > 0
      ? pricingItems[0].products
      : null;

  return (
    <Box className={clsx(className, "pricing-breakdown")}>
      {modalTitle ? (
        useHeader ? (
          <Box className="pricing-header-container">
            <Typography className="pricing-header" variant="h6">
              {modalTitle}
            </Typography>
          </Box>
        ) : (
          <div className="price-breakdown-modal-title">{modalTitle}</div>
        )
      ) : null}

      <Slot id="price-breakdown-additional-info" />
      {pricingItems && <Box className="pricing-items-section">{LineItems}</Box>}
      {displayProductsSeparately && products && (
        <Box className="pricing-items-section">
          <Box className={clsx("pricing-item-wrapper", className)}>
            <Box className={clsx("pricing-item-body", { "with-icon": false })}>
              <Box className="content-section">
                <Products products={products} />
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <Box className="summary-items-section">
        {summaryItems.map((item, index) => (
          <SummaryItem
            key={`summary-item-${index}`}
            summaryLineItem={item}
            titles={{
              totalText: titles.totalText,
            }}
            roundingMode={roundingMode}
          />
        ))}
      </Box>
      <Slot id="price-breakdown-discount-price-line" />
      {currencyString ? (
        <div className="trip-total-currency">{currencyString}</div>
      ) : null}
      {showSecurePaymentsBanner && (
        <SecurePaymentsBanner className="price-breakdown-secure-banner" />
      )}
    </Box>
  );
};
